import { createDataTable } from "./data-table.js";
import { createFormPage } from "./form-page.js";
import { requestId } from "./request-id.js";
import { setBusy, button as uiButton } from "./ui/button.js";
import { field, form as formElement, validate } from "./ui/field.js";
import { emptyState, section } from "./ui/page.js";

const el = (tag, text) => {
  const node = document.createElement(tag);
  if (text !== undefined) node.textContent = text;
  return node;
};
/**
 * Private notes (Knowledge) and shared project notes. ADM-02: a new note is
 * written on its own page (`page.name`, e.g. `#/knowledge/new`); a saved note
 * is read, revised and forgotten in its row's inspector.
 *
 * @param {{ client: any, container: HTMLElement, report?: (message: string) => void,
 *   confirm?: (message: string) => Promise<boolean>, changed?: () => void,
 *   page?: { name: string, list: string | (() => string), backText: string },
 *   onNew?: () => void, level?: 2 | 3, sectionAction?: boolean }} options
 *
 * Without `page`, the caller owns the create page: "New note" calls `onNew`,
 * and `noteForm()` builds the form it shows (a project's `notes/new`).
 */
export function createScopedNotesUI({
  client,
  container,
  report = () => {},
  confirm = async () => false,
  changed = () => {},
  page,
  onNew,
  level = 2,
  sectionAction = true,
}) {
  let context = {},
    epoch = 0,
    inspector = 0;
  const path = () => ({
    tenantId: context.tenantId,
    ...(context.projectId ? { projectId: context.projectId } : {}),
  });
  const call = (verb, params, body) =>
    client[
      `${verb}${context.projectId ? "Project" : ""}Note${verb === "list" ? "s" : ""}`
    ](params, body);
  const writable = () =>
    Boolean(context.tenantId && context.userId && context.role !== "viewer");
  function button(text, action) {
    const captured = epoch;
    return uiButton({
      text,
      onClick: () => {
        if (captured === epoch)
          void action().catch((error) => {
            if (captured === epoch) report(error.message);
          });
      },
    });
  }
  // The create page: title and content, saved once with a stable request id.
  const createPage =
    page &&
    createFormPage({
      name: page.name,
      list: page.list,
      backText: page.backText,
      title: "New note",
      description: "",
      submitText: "Save note",
      allowed: writable,
      onShow: () => {
        const shared = Boolean(context.projectId);
        createPage.setTitle(
          shared ? "New project note" : "New private note",
          shared
            ? "Shared with this project's authorized bots. Editing or forgetting it later invalidates existing retrieval pins."
            : "Only you can read it. Saving does not share it: allow retrieval for an exact note in a personal bot conversation.",
        );
        createPage.submit.querySelector(".ui-button__label").textContent =
          shared ? "Save and share project note" : "Save private note";
        draftRequest = requestId();
      },
      onClose: () => {
        newTitle.value = newContent.value = "";
      },
      onSubmit: async () => {
        const captured = epoch;
        try {
          await call("create", path(), {
            requestId: draftRequest,
            title: newTitle.value,
            content: newContent.value,
            confirmed: true,
          });
        } catch (error) {
          if (captured === epoch) report(error.message);
          throw error;
        }
        if (captured !== epoch) return false;
        newTitle.value = newContent.value = "";
        draftRequest = requestId();
        changed();
      },
    });
  let draftRequest = requestId();
  const newTitle = noteTitle(),
    newContent = noteContent();
  createPage?.fields.append(
    field({ label: "Title", control: newTitle }),
    field({ label: "Content", control: newContent }),
  );
  const newNote = () =>
    (createPage || onNew) &&
    uiButton({
      text: "New note",
      icon: "plus",
      variant: "primary",
      onClick: () =>
        onNew ? onNew() : createPage?.open(context.projectId || ""),
    });
  /**
   * The create form for a caller-owned page: title and content, saved once
   * with a stable request id; `onDone` runs after saving or Cancel.
   * @param {{ onDone: () => void }} options
   */
  function noteForm({ onDone }) {
    const captured = epoch,
      params = path(),
      id = requestId(),
      title = noteTitle(),
      content = noteContent(),
      save = uiButton({
        text: context.projectId
          ? "Save and share project note"
          : "Save private note",
        variant: "primary",
        type: "submit",
      });
    const form = formElement(
      { class: "ui-form-page__form scoped-note-form" },
      el("fieldset"),
      el("div"),
    );
    const [fields, actions] = form.children;
    fields.className = "ui-form-fields";
    actions.className = "ui-form-page__actions";
    fields.append(
      field({ label: "Title", control: title }),
      field({ label: "Content", control: content }),
    );
    actions.append(
      uiButton({ text: "Cancel", variant: "secondary", onClick: onDone }),
      save,
    );
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      if (captured !== epoch || !validate(form)) return;
      setBusy(save, true);
      void (async () => {
        try {
          await call("create", params, {
            requestId: id,
            title: title.value,
            content: content.value,
            confirmed: true,
          });
          if (captured !== epoch) return;
          changed();
          onDone();
          await load();
        } catch (error) {
          if (captured === epoch) report(error.message);
        } finally {
          setBusy(save, false);
        }
      })();
    });
    return form;
  }

  async function load() {
    const captured = ++epoch;
    if (!context.tenantId || !context.userId) {
      container.replaceChildren();
      return;
    }
    const rows = await call("list", path());
    if (captured !== epoch) return;
    const table = el("div");
    const canWrite = context.role !== "viewer";
    container.replaceChildren(
      section(
        {
          title: context.projectId
            ? "Shared project notes"
            : "Your private notes",
          description: context.projectId
            ? "Notes saved here are explicitly shared with authorized project bots. Editing or forgetting invalidates existing retrieval pins."
            : "These notes belong only to you. Select an exact note below to allow retrieval in a personal bot conversation. Saving does not share it automatically.",
          actions: [sectionAction && canWrite && newNote()],
          level,
          flush: true,
        },
        table,
      ),
    );
    createDataTable({
      host: table,
      label: "Saved notes",
      empty: emptyState({
        icon: "file",
        title: "No notes yet",
        body: canWrite
          ? "Write a note with facts or preferences your bunnies may use."
          : "Notes saved here appear for everyone with access.",
        compact: true,
      }),
      columns: [
        { id: "title", label: "Note", value: (row) => row.title },
        { id: "revision", label: "Revision", value: (row) => row.revision },
        { id: "status", label: "Status", value: (row) => row.status },
      ],
      detail: (row) => {
        const version = ++inspector;
        const valid = () => version === inspector;
        const panel = el("div");
        panel.append(
          el(
            "p",
            `Author ${row.ownerUserId} · revision ${row.revision} · SHA-256 ${row.contentHash}`,
          ),
          el("pre", row.content),
        );
        if (
          row.status === "active" &&
          row.canEdit &&
          context.role !== "viewer"
        ) {
          panel.append(
            editor(row, valid),
            button("Forget note", async () => {
              if (!valid()) return;
              const captured = epoch,
                params = path();
              if (
                !(await confirm(
                  `Forget ${row.title}? Future retrieval stops. Accepted transcripts, audits and backups retain their own history.`,
                )) ||
                captured !== epoch ||
                !valid()
              )
                return;
              await call(
                "forget",
                { ...params, noteId: row.id },
                { expectedRevision: row.revision, confirmed: true },
              );
              if (captured !== epoch) return;
              await load();
              changed();
            }),
          );
        }
        return panel;
      },
    }).setData(rows);
  }
  function noteTitle() {
    const title = el("input");
    title.className = "ui-input";
    title.required = true;
    title.maxLength = 120;
    title.placeholder = "Note title";
    return title;
  }
  function noteContent() {
    const content = el("textarea");
    content.className = "ui-input";
    content.required = true;
    content.maxLength = 2000;
    content.rows = 4;
    content.placeholder = "Explicitly saved facts or preferences…";
    return content;
  }
  /** A revision form in a note's inspector (or, without a create page, the inline create form). */
  function editor(row, valid = () => true) {
    const form = el("form"),
      title = noteTitle(),
      content = noteContent(),
      save = uiButton({
        text: row
          ? "Save revision"
          : context.projectId
            ? "Save and share project note"
            : "Save private note",
        type: "submit",
      }),
      captured = epoch,
      params = path();
    form.className = "work-form";
    title.setAttribute("aria-label", "Note title");
    content.setAttribute("aria-label", "Note content");
    title.value = row?.title || "";
    content.value = row?.content || "";
    form.append(title, content, save);
    const id = requestId();
    form.onsubmit = (event) => {
      event.preventDefault();
      if (captured !== epoch || !valid()) return;
      save.disabled = true;
      void (async () => {
        try {
          await call(
            row ? "update" : "create",
            { ...params, ...(row ? { noteId: row.id } : {}) },
            row
              ? {
                  expectedRevision: row.revision,
                  title: title.value,
                  content: content.value,
                }
              : {
                  requestId: id,
                  title: title.value,
                  content: content.value,
                  confirmed: true,
                },
          );
          if (captured !== epoch) return;
          await load();
          changed();
        } catch (error) {
          if (captured === epoch) {
            save.disabled = false;
            report(error.message);
          }
        }
      })();
    };
    return form;
  }
  return {
    load,
    noteForm,
    /** Whether the current person may write a note here. */
    get canCreate() {
      return Boolean(createPage) && writable();
    },
    /** Open the create page (when this list has one). */
    openCreate() {
      createPage?.open(context.projectId || "");
    },
    updateContext(next) {
      if (
        ["tenantId", "projectId", "userId", "role"].some(
          (key) => context[key] !== next[key],
        )
      ) {
        epoch++;
        container.replaceChildren();
        if (createPage) {
          newTitle.value = newContent.value = "";
          draftRequest = requestId();
        }
      }
      context = { ...next };
    },
    dispose() {
      epoch++;
      container.replaceChildren();
    },
  };
}
